import { Currency } from '@principle-theorem/accounting';
import {
  DepositCaptureStrategy,
  DepositPaymentMode,
  DepositStatus,
  type IAppointment,
  type IChartedTreatment,
  type IDepositMetadata,
  type IDepositSettings,
} from '@principle-theorem/principle-core/interfaces';

/**
 * Resolves a charted treatment's deposit indicator so treatment surfaces use
 * the same configuration and paid-state eligibility rules.
 */
export class ChartedTreatmentDeposit {
  /**
   * Produces the display metadata because charted treatments must not imply a
   * payment when the current configuration no longer matches the appointment.
   */
  static resolve(
    treatment: IChartedTreatment,
    treatments: readonly IChartedTreatment[],
    settings: IDepositSettings | undefined,
    appointment: Pick<IAppointment, 'deposit'> | undefined,
    currency: Currency
  ): IDepositMetadata | undefined {
    if (!settings?.enabled) {
      return;
    }

    const configurationDeposit =
      settings.treatmentConfigurationDeposits?.[treatment.config.ref.id];
    if (!configurationDeposit) {
      return;
    }

    const appointmentDeposit = appointment?.deposit?.metadata;
    const paidConfigurationDeposit =
      appointmentDeposit?.treatmentConfigurationDeposits?.[
        treatment.config.ref.id
      ];
    const currentQuantity = treatments.filter(
      (candidate) => candidate.config.ref.id === treatment.config.ref.id
    ).length;
    const matchesPaidDeposit =
      paidConfigurationDeposit?.amountCents ===
        configurationDeposit.amountCents &&
      paidConfigurationDeposit.quantity === currentQuantity;
    if (appointmentDeposit && !matchesPaidDeposit) {
      return;
    }

    const matchedAppointmentDeposit = matchesPaidDeposit
      ? appointmentDeposit
      : undefined;
    return {
      status: matchedAppointmentDeposit?.status ?? DepositStatus.Required,
      amountCents: configurationDeposit.amountCents,
      currency,
      paymentMode: DepositPaymentMode.Charge,
      captureStrategy: DepositCaptureStrategy.ChargeOnBookingRequest,
      description: 'Captured immediately',
      ...(matchedAppointmentDeposit?.paidAt
        ? { paidAt: matchedAppointmentDeposit.paidAt }
        : {}),
    };
  }
}
